import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../api/axios';
import { useAuth } from '../../contexts/KeycloakContext';

/**
 * AcceptInvite
 * Handles the redirect_uri that Keycloak sends existing users to after the
 * execute-actions-email link is clicked.
 *
 * URL format:  /workspace/accept-invite?token=<invite-token>
 *
 * Flow:
 *  1. If not authenticated → save token in sessionStorage and go to /login.
 *  2. After login the user returns here (App.js restores the last route).
 *  3. When authenticated → POST /api/workspace/invite/:token/accept.
 *  4. On success → redirect to /  (workspace context will reload).
 */
const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const { authenticated } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState('pending'); // 'pending' | 'accepting' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const acceptedRef = useRef(false);

  const token = searchParams.get('token') || sessionStorage.getItem('pendingInviteToken');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid invitation link. Please ask your admin to resend the invitation.');
      return;
    }

    if (!authenticated) {
      // Not logged in — save token and go to login
      sessionStorage.setItem('pendingInviteToken', token);
      navigate(
        `/login?message=${encodeURIComponent('Please log in to accept your workspace invitation.')}`,
        { replace: true }
      );
      return;
    }

    // Authenticated — accept the invite once
    if (acceptedRef.current) return;
    acceptedRef.current = true;

    // Clear the saved token now that we have a session
    sessionStorage.removeItem('pendingInviteToken');

    setStatus('accepting');

    axiosInstance
      .post(`/workspace/invite/${token}/accept`)
      .then((res) => {
        if (res.data.success) {
          setWorkspaceName(res.data.data?.workspaceName || 'the workspace');
          setStatus('success');
          setMessage(res.data.message || 'Invitation accepted!');
          // Redirect to dashboard after a short pause
          setTimeout(() => navigate('/', { replace: true }), 2500);
        } else {
          setStatus('error');
          setMessage(res.data.message || 'Failed to accept invitation.');
        }
      })
      .catch((err) => {
        const msg =
          err.response?.data?.message || 'Failed to accept invitation. The link may have expired.';
        setStatus('error');
        setMessage(msg);
      });
  }, [authenticated, token, navigate]);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (status === 'pending' || status === 'accepting') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <i
            className="fas fa-spinner fa-spin"
            style={{ fontSize: 36, color: '#4f46e5', marginBottom: 16 }}
          ></i>
          <h2 style={styles.title}>Accepting Invitation…</h2>
          <p style={styles.subtitle}>Please wait while we set up your access.</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <i
            className="fas fa-check-circle"
            style={{ fontSize: 48, color: '#16a34a', marginBottom: 16 }}
          ></i>
          <h2 style={styles.title}>Welcome!</h2>
          <p style={styles.subtitle}>
            You have successfully joined <strong>{workspaceName}</strong>.
          </p>
          <p style={{ color: '#94a3b8', fontSize: 14, marginTop: 8 }}>
            Redirecting to the dashboard…
          </p>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <i
          className="fas fa-exclamation-circle"
          style={{ fontSize: 48, color: '#ef4444', marginBottom: 16 }}
        ></i>
        <h2 style={styles.title}>Invitation Error</h2>
        <p style={styles.subtitle}>{message}</p>
        <button style={styles.btn} onClick={() => navigate('/', { replace: true })}>
          Go to Dashboard
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    padding: '48px 40px',
    textAlign: 'center',
    maxWidth: 440,
    width: '100%',
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#0f172a',
    margin: '0 0 12px',
  },
  subtitle: {
    fontSize: 16,
    color: '#475569',
    margin: 0,
  },
  btn: {
    marginTop: 24,
    padding: '10px 28px',
    background: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default AcceptInvite;
